// shared/constants/adStyles.ts

// Originally 18 styles — added "Testimonial" because the winning_ads DB has
// 4 ads tagged that way. The other graphic/aesthetic styles users name
// (Text-on-Image, Flat Lay, Comparison Chart, Lifestyle Shot, Split Screen)
// have ZERO winning-ads coverage so they don't belong here — they bypass
// AD_STYLES_18 entirely via the classifier's requestedProductionStyles
// field, which locks productionStyle directly without going through the
// anchor-mirror mechanic. Constant name kept as AD_STYLES_18 to avoid
// downstream rename churn.
export const AD_STYLES_18 = [
  "Problem-Solution",
  "Benefits",
  "Features",
  "Reviews",
  "Listicle",
  "Advertorial",
  "Us vs Them",
  "Education",
  "UGC Style",
  "Before & After",
  "Founder Story",
  "Social Proof",
  "Urgency",
  "Product Launch",
  "Sale",
  "How It Works",
  "Myth Busting",
  "Dark Mode",
  "Testimonial",
] as const;

export const AD_ANGLES_18 = [
  "Pain Point",
  "Lifestyle",
  "Testimonial",
  "Authority",
  "Transformation",
  "Curiosity Gap",
  "Scarcity",
  "Risk Reversal",
  "Aspiration",
  "Identity",
  "Comparison",
  "Direct Response",
  "Storytelling",
  "Contrarian",
  "Stats Hook",
  "FOMO",
  "Emotional",
  "Humor",
] as const;

export type AdStyle = (typeof AD_STYLES_18)[number];
export type AdAngle = (typeof AD_ANGLES_18)[number];

/**
 * Fuzzy aliases map informal user terms to canonical style names.
 * Keys MUST be lowercase. Longer aliases are checked first to avoid
 * partial matches (e.g. "social proof" before "social").
 */
export const STYLE_ALIASES: Record<string, AdStyle> = {
  "problem solution": "Problem-Solution",
  "problem-solution": "Problem-Solution",
  "benefits": "Benefits",
  "benefit": "Benefits",
  "features": "Features",
  "feature": "Features",
  "reviews": "Reviews",
  "review": "Reviews",
  "listicle": "Listicle",
  "advertorial": "Advertorial",
  "us vs them": "Us vs Them",
  "us versus them": "Us vs Them",
  "education": "Education",
  "educational": "Education",
  "ugc style": "UGC Style",
  "ugc": "UGC Style",
  "user generated": "UGC Style",
  "before & after": "Before & After",
  "before and after": "Before & After",
  "b&a": "Before & After",
  "founder story": "Founder Story",
  "founder": "Founder Story",
  "social proof": "Social Proof",
  "urgency": "Urgency",
  "urgent": "Urgency",
  "product launch": "Product Launch",
  "sale": "Sale",
  "how it works": "How It Works",
  "myth busting": "Myth Busting",
  "myth bust": "Myth Busting",
  "dark mode": "Dark Mode",
  "testimonial": "Testimonial",
  "testimonials": "Testimonial",
  "quote ad": "Testimonial",
};

export const ANGLE_ALIASES: Record<string, AdAngle> = {
  "pain point": "Pain Point",
  "painpoint": "Pain Point",
  "lifestyle": "Lifestyle",
  "testimonial": "Testimonial",
  "authority": "Authority",
  "transformation": "Transformation",
  "curiosity gap": "Curiosity Gap",
  "curiosity": "Curiosity Gap",
  "scarcity": "Scarcity",
  "risk reversal": "Risk Reversal",
  "aspiration": "Aspiration",
  "aspirational": "Aspiration",
  "identity": "Identity",
  "comparison": "Comparison",
  "direct response": "Direct Response",
  "dr": "Direct Response",
  "storytelling": "Storytelling",
  "contrarian": "Contrarian",
  "stats hook": "Stats Hook",
  "stats": "Stats Hook",
  "fomo": "FOMO",
  "emotional": "Emotional",
  "humor": "Humor",
  "humour": "Humor",
  "funny": "Humor",
};

export interface TargetedConstraints {
  requestedStyles: AdStyle[];
  requestedAngles: AdAngle[];
  creativeDirection: string;
  referenceImageMode: "inspiration" | null;
  // Direct productionStyle lock from the classifier — bypasses AD_STYLES_18.
  // Set when the user explicitly names a graphic/aesthetic format that doesn't
  // have AD_STYLES_18 coverage (e.g. "Text-on-Image", "Flat Lay").
  requestedProductionStyles?: string[];
}

/**
 * Scans a user message for style/angle keywords using fuzzy alias matching.
 * Returns null if no styles or angles are detected.
 *
 * Alias keys are sorted longest-first so "social proof" matches before "social"
 * would (if "social" were an alias — it isn't, but the sort prevents future bugs).
 */
export function detectStylesAndAngles(message: string): TargetedConstraints | null {
  const lower = message.toLowerCase();

  const detectedStyles: AdStyle[] = [];
  const sortedStyleAliases = Object.keys(STYLE_ALIASES).sort((a, b) => b.length - a.length);
  for (const alias of sortedStyleAliases) {
    if (lower.includes(alias)) {
      const canonical = STYLE_ALIASES[alias];
      if (!detectedStyles.includes(canonical)) {
        detectedStyles.push(canonical);
      }
    }
  }

  const detectedAngles: AdAngle[] = [];
  const sortedAngleAliases = Object.keys(ANGLE_ALIASES).sort((a, b) => b.length - a.length);
  for (const alias of sortedAngleAliases) {
    if (lower.includes(alias)) {
      const canonical = ANGLE_ALIASES[alias];
      if (!detectedAngles.includes(canonical)) {
        detectedAngles.push(canonical);
      }
    }
  }

  if (detectedStyles.length === 0 && detectedAngles.length === 0) {
    return null;
  }

  // Build creative direction: strip matched aliases + common ad-creation verbs/counts
  let remaining = lower;
  for (const alias of [...sortedStyleAliases, ...sortedAngleAliases]) {
    if (remaining.includes(alias)) {
      remaining = remaining.replace(alias, " ");
    }
  }
  // Strip creation verbs, counts, filler words
  remaining = remaining
    .replace(/\b(make|create|generate|build|run|give me|show me)\b/gi, "")
    .replace(/\b(me|some|a few|the)\b/gi, "")
    .replace(/\b\d+\b/g, "")
    .replace(/\b(ads?|creatives?|images?|style|angle)\b/gi, "")
    .replace(/\b(like this one|like this|for)\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();

  return {
    requestedStyles: detectedStyles,
    requestedAngles: detectedAngles,
    creativeDirection: remaining,
    referenceImageMode: null, // set by orchestrator based on agentCtx.referenceImages
  };
}

/**
 * v7.37 — composer "Advanced" filter (Dan's spec): the user can restrict a
 * run to a subset of angles and/or styles. null on an axis = All (today's
 * behavior). Travels as body.adStyles / body.adAngles → ctx.adFilter and is
 * honored inside WHICHEVER campaign path fires (concepts-first, campaign-v2,
 * targeted) — it never re-routes the request.
 */
export interface AdFilter {
  styles: AdStyle[] | null;
  angles: AdAngle[] | null;
}

function normalizeAxis<T extends string>(raw: unknown, canon: readonly T[]): T[] | null {
  if (!Array.isArray(raw)) return null;
  const byLower = new Map(canon.map((c) => [c.toLowerCase(), c] as const));
  const out: T[] = [];
  for (const v of raw) {
    if (typeof v !== "string") continue;
    const hit = byLower.get(v.trim().toLowerCase());
    if (hit && !out.includes(hit)) out.push(hit);
  }
  // Empty or the full set both mean "All" — no constraint to carry.
  if (out.length === 0 || out.length >= canon.length) return null;
  return out;
}

/** Case-insensitive exact match against the canonical lists; junk dropped,
 *  duplicates collapsed, empty/full axes read as null (= All). */
export function normalizeAdFilter(styles: unknown, angles: unknown): AdFilter {
  return {
    styles: normalizeAxis(styles, AD_STYLES_18),
    angles: normalizeAxis(angles, AD_ANGLES_18),
  };
}
